/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { LuActivity, LuCalendarDays, LuChevronsDown, LuChevronsUp, LuCircleDot, LuHand, LuRepeat, LuTarget, LuTrendingUp, LuTrophy, LuUsers, LuZap } from "react-icons/lu";
import { getPlayerImageUrl } from "../../helper/helper";
import { codeFor, colorFor, startsIn } from "../tournamentPage/matchVm";
import { IcPlay, IcSrchL } from "../tournamentPage/icons";
import { CountdownBox } from "../tournamentPage/MatchesPanel";
import { useCountdown } from "../tournamentPage/useCountdown";
import { norm, teamPath, useTournamentTeams } from "../teamsTab/teamsData";
import { TeamCrest } from "../teamsTab/TeamsTab";
import { fmtDay, fmtLong, isMultiDay, nice, nrrText, quotientText, useGroupTables, useHomeSummary, useSponsors, useTournamentInfo } from "./tournamentData";
import "../tournamentPage/tournamentPage.scss";
import "../teamsTab/teamsTab.scss";
import "./tournamentTabs.scss";

type Props = { tournamentId: string; tournamentName: string; onTab: (k: string) => void };

const Glyph = ({ t }: { t: string }) => <b className="glyph">{t}</b>;
// stat type (as the API names it) -> label, colour, icon
const STATS: [string, string, string, ReactNode][] = [
  ["players", "Players", "#1D3FD1", <LuUsers key="i" />],
  ["matches", "Matches", "#E3214F", <LuCalendarDays key="i" />],
  ["runs", "Runs", "#F0802A", <LuTrendingUp key="i" />],
  ["wickets", "Wickets", "#E3214F", <LuTarget key="i" />],
  ["balls", "Balls", "#0E9A48", <LuCircleDot key="i" />],
  ["hatTricks", "Hat-trick", "#1D3FD1", <LuZap key="i" />],
  ["fours", "Fours", "#1D3FD1", <Glyph key="i" t="4" />],
  ["sixes", "Sixes", "#F0802A", <Glyph key="i" t="6" />],
  ["fifties", "50s", "#E3214F", <Glyph key="i" t="50" />],
  ["hundreds", "100s", "#0E9A48", <Glyph key="i" t="100" />],
  ["catches", "Catches", "#E3214F", <LuHand key="i" />],
  ["highestTeamScore", "Highest Team Score", "#F0802A", <LuChevronsUp key="i" />],
  ["lowestTeamScore", "Lowest Team Score", "#1D3FD1", <LuChevronsDown key="i" />],
  ["firstInningAvgRuns", "First Inn Avg Runs", "#0E9A48", <LuActivity key="i" />],
  ["firstInningAvgWickets", "First Inn Avg Wickets", "#E3214F", <LuActivity key="i" />],
  ["runOuts", "Run-Out", "#F0802A", <LuRepeat key="i" />],
  ["ducks", "Ducks", "#E3214F", <Glyph key="i" t="0" />],
  ["extras", "Extras", "#1F2128", <Glyph key="i" t="Ex" />],
];

const initials = (n: string) => n.split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const fmtNum = (v: unknown) => (v == null || v === "" ? "–" : isNaN(Number(v)) ? String(v) : Number(v).toLocaleString("en-US"));
const scoreText = (s?: { runs?: number; wickets?: number; overs?: string | number } | null) =>
  s && s.runs != null ? `${s.runs}${s.wickets != null && s.wickets < 10 ? `/${s.wickets}` : ""}${s.overs != null && s.overs !== "" ? ` (${s.overs})` : ""}` : "";

const Sec = ({ title, link, onLink, children }: { title: string; link?: string; onLink?: () => void; children: ReactNode }) => (
  <section className="hsec">
    <div className="hsh"><h2>{title}</h2>{link && onLink && <button className="hlink" onClick={onLink}>{link}</button>}</div>
    {children}
  </section>
);

const FeaturedCard = ({ f }: { f: any }) => {
  const navigate = useNavigate();
  const status = String(f.matchStatus ?? "").toUpperCase();
  const live = status === "IN_PROGRESS", past = status === "ENDED";
  const parsed = f.startDate ? new Date(f.startDate) : null;
  const start = parsed && !isNaN(parsed.getTime()) ? parsed : null;
  // within 24h of the start, upcoming cards tick down like the Matches tab cards
  const cd = useCountdown(start, !live && !past);
  const when = start ? `${startsIn(start).when}, ${start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "";
  const line = (f.statusText ?? "").trim() || (!past && !live && start && !cd ? startsIn(start).text : "");
  const side = (t: any, sc: any) => (
    <div className="fside">
      <TeamCrest t={{ name: t?.name ?? "-", code: codeFor(t?.name, t?.teamCode), color: colorFor(t?.name), image: t?.logoUrl || undefined }} size={28} />
      <span className="fn">{t?.name ?? "-"}</span>
      <span className="fs">{scoreText(sc)}</span>
    </div>
  );
  return (
    <a className={`fcard ${live ? "live" : past ? "past" : "up"}`} href={`/matches-details/${f.id}`} onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(`/matches-details/${f.id}`); }}>
      <div className="ftop">
        {live ? <span className="ftag lv"><i />LIVE</span> : <span className="ftag">{past ? "RESULT" : "UPCOMING"}</span>}
        {live && f.hasLiveStream && <span className="fplay" title="Watch live"><IcPlay /></span>}
        <span className="fwhen">{past && start ? fmtDay(start) : !live ? when : ""}</span>
      </div>
      {side(f.teamOne, f.teamOneScore)}
      {side(f.teamTwo, f.teamTwoScore)}
      {cd && <CountdownBox cd={cd} />}
      {line && <div className="fline">{line}</div>}
      <div className="ffoot"><span>{f.groundName ?? ""}</span><span className="go">{past ? "Match centre" : live ? "Live match" : "Match preview"} ›</span></div>
    </a>
  );
};

const Skeleton = () => (
  <div className="hm-skel" aria-hidden="true"><i className="sk" style={{ height: 150 }} /><div className="sgrid">{Array.from({ length: 18 }, (_, i) => <i key={i} className="sk" style={{ height: 84 }} />)}</div></div>
);

const HomeTab = ({ tournamentId, tournamentName, onTab }: Props) => {
  const navigate = useNavigate();
  const sumQ = useHomeSummary(tournamentId);
  const infoQ = useTournamentInfo(tournamentId);
  const teamsQ = useTournamentTeams(tournamentId);
  const sponsorsQ = useSponsors(tournamentId);
  const sum = sumQ.data;
  const info = infoQ.data;

  // Points preview: the first division, one pill per group
  const firstDiv = teamsQ.data?.divisions?.[0];
  const groups = useMemo(() => (firstDiv ? (firstDiv.groups.length ? firstDiv.groups : [{ id: firstDiv.id, name: firstDiv.name }]) : []), [firstDiv]);
  const tables = useGroupTables(firstDiv?.id);
  const [groupId, setGroupId] = useState<string>("");
  const activeGroup = groups.find((g) => g.id === groupId) ?? groups[0];
  const rows = (activeGroup ? tables.data?.[activeGroup.id] : undefined) ?? [];
  const isTest = isMultiDay(info);
  // the points API carries no logo: take it from the tournament's teams, then from the home summary's team list
  const logoOf = (snapshotId: string, name: string) =>
    teamsQ.data?.teams.find((t) => t.snapshotId === snapshotId)?.image ?? (sumQ.data?.teams ?? []).find((t: any) => t.teamSnapshotId === snapshotId || norm(t.name) === norm(name))?.logoUrl ?? undefined;

  if (sumQ.isLoading) return <div className="tp hm"><main className="wrap main"><Skeleton /></main></div>;
  if (sumQ.isError || !sum)
    return (
      <div className="tp hm"><main className="wrap main"><div className="empty"><span className="ic"><IcSrchL /></span><h3>Couldn't load this</h3><p>Check your connection and try again. Nothing you've saved is lost.</p><div className="acts"><button className="btn primary" onClick={() => void sumQ.refetch()}>Try again</button></div></div></main></div>
    );

  const featured: any[] = sum.featuredMatches ?? [];
  const stats: Record<string, any> = Object.fromEntries((sum.stats ?? []).map((s: any) => [s.type, s]));
  const perf: any[] = sum.playerPerformance ?? [];
  const teams: any[] = sum.teams ?? [];
  const sponsors = sponsorsQ.data ?? [];
  const noMatchesYet = !stats.matches || Number(stats.matches.value) === 0;
  const openPlayer = (id?: string) => { if (id) navigate(`/tournaments/player-profile?playerId=${encodeURIComponent(id)}`); };

  const infoRows: [string, string][] = [
    ["Tournament Name", info?.tournamentName ?? tournamentName],
    info?.organizerName ? ["Organizer Name", info.organizerName] : null,
    info?.startDate ? ["Start Date", fmtLong(info.startDate)] : null,
    info?.endDate ? ["End Date", fmtLong(info.endDate)] : null,
    info?.country ? ["Location", info.country] : null,
    ["Number of Teams", String(teams.length)],
    info?.matchType ? ["Format", `${nice(info.matchType)}${info.maxOvers && !isTest ? ` · ${info.maxOvers} overs` : ""}`] : null,
    info?.ballType ? ["Ball", nice(info.ballType)] : null,
  ].filter(Boolean) as [string, string][];

  return (
    <div className="tp hm">
      <main className="wrap main">
        <Sec title="Featured Matches" link="All Matches" onLink={() => onTab("matches")}>
          {featured.length === 0 ? (
            <div className="hempty"><b>{teams.length === 0 ? "Teams and fixtures coming soon" : "Fixtures coming soon"}</b><span>Featured matches show here once the organiser publishes the schedule.</span></div>
          ) : <div className="fgrid">{featured.map((f) => <FeaturedCard key={f.id} f={f} />)}</div>}
        </Sec>

        <Sec title="Stats" link="View All" onLink={() => onTab("stats")}>
          <div className="sgrid">
            {STATS.filter(([k]) => stats[k]).map(([k, label, color, icon]) => (
              <div className="stile" key={k} style={{ ["--sc" as any]: color }}>
                <div className="stx"><b>{fmtNum(stats[k].value)}</b><span>{label}</span></div>
                <span className="sic" aria-hidden="true">{icon}</span>
              </div>
            ))}
          </div>
          {noMatchesYet && <p className="hnote">All stats are 0 until the first match is played.</p>}
        </Sec>

        <Sec title="Player Performance" link="View All" onLink={() => onTab("stats")}>
          {perf.length === 0 ? (
            <div className="pgrid3">{["Most Valuable Player", "Most Runs", "Most Wickets"].map((l) => (
              <div className="pfc off" key={l}><span className="pfl"><LuTrophy />{l}</span><div className="pfp"><span className="pav">–</span><span><b>To be decided</b><small>After the first match</small></span></div><div className="pfv"><b>0</b></div></div>
            ))}</div>
          ) : (
            <div className="pgrid3">
              {perf.map((p) => {
                const pl = p.player ?? {};
                const url = getPlayerImageUrl(pl.photoUrl) ?? "";
                return (
                  <button className="pfc" key={p.category} onClick={() => openPlayer(pl.id)}>
                    <span className="pfl"><LuTrophy />{p.label}</span>
                    <div className="pfp">
                      <span className="pav">{url ? <img src={url} alt="" /> : initials(pl.name ?? "")}</span>
                      <span><b>{pl.name}</b><small>{pl.teamName}</small></span>
                    </div>
                    <div className="pfv"><b>{fmtNum(p.value)}</b><small>{String(p.unit ?? "").toUpperCase()}</small></div>
                  </button>
                );
              })}
            </div>
          )}
        </Sec>

        <div className="hcols">
          <Sec title="Points Table" link="View Full Table" onLink={() => onTab("points")}>
            {groups.length > 1 && (
              <div className="gpills" role="tablist" aria-label="Group">
                {groups.map((g) => <button key={g.id} role="tab" aria-selected={activeGroup?.id === g.id} className={activeGroup?.id === g.id ? "on" : ""} onClick={() => setGroupId(g.id)}>{g.name}</button>)}
              </div>
            )}
            {tables.isLoading || infoQ.isLoading ? <i className="sk" style={{ height: 180, display: "block", borderRadius: 14 }} /> : rows.length === 0 ? (
              <div className="hempty"><b>No points table yet</b><span>It appears once teams are added.</span></div>
            ) : (
              <div className="box"><div className="tw"><table className="hpt">
                <thead><tr><th className="l">Teams</th><th>P</th><th>W</th><th>L</th><th>{isTest ? "D" : "T"}</th>{!isTest && <th>N/R</th>}<th>{isTest ? "Quotient" : "NRR"}</th><th className="pt">Pts</th></tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r.teamSnapshotId || r.name} className="click" tabIndex={0} onClick={() => navigate(teamPath(tournamentId, r.teamSnapshotId))} onKeyDown={(e) => { if (e.key === "Enter") navigate(teamPath(tournamentId, r.teamSnapshotId)); }}>
                    <td className="l"><span className="hteam"><TeamCrest t={{ name: r.name, code: codeFor(r.name), color: colorFor(r.name), image: logoOf(r.teamSnapshotId, r.name) }} size={24} /><span className="hn">{r.name}</span></span></td>
                    <td>{r.m}</td><td>{r.w}</td><td>{r.l}</td><td>{isTest ? r.d : r.t}</td>{!isTest && <td>{r.nr}</td>}<td>{isTest ? quotientText(r.nrr) : r.nrr == null ? "0.000" : nrrText(r.nrr)}</td><td className="pt">{r.pts + r.bonus}</td>
                  </tr>))}</tbody>
              </table></div></div>
            )}
          </Sec>
          <Sec title="Tournament Info" link="More" onLink={() => onTab("about")}>
            <div className="box hinfo">{infoRows.map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}</div>
          </Sec>
        </div>

        <Sec title="Teams" link={teams.length ? "View All" : undefined} onLink={() => onTab("teams")}>
          {teams.length === 0 ? (
            <div className="hempty"><b>Teams haven't been announced yet</b><span>The organiser is still adding teams.</span></div>
          ) : (
            <div className="ttiles">{teams.map((t) => (
              <button key={t.teamSnapshotId} className="ttile" onClick={() => navigate(teamPath(tournamentId, t.teamSnapshotId))}>
                <TeamCrest t={{ name: t.name, code: codeFor(t.name, t.teamCode), color: colorFor(t.name), image: t.logoUrl || undefined }} size={56} />
                <span>{t.name}</span>
              </button>))}</div>
          )}
        </Sec>

        <Sec title="Sponsors" link={sponsors.length ? "View All" : undefined} onLink={() => onTab("about")}>
          {sponsors.length === 0 ? (
            <div className="hempty"><b>No sponsors yet</b><span>Sponsors show here when the organiser adds them.</span></div>
          ) : (
            <div className="sptiles">{sponsors.map((s) => {
              const url = getPlayerImageUrl(s.image) ?? "";
              return <div className="sptile" key={s.id} title={s.role ? `${s.name} · ${s.role}` : s.name}>{url ? <img src={url} alt={s.name} /> : <b>{s.name}</b>}<span>{s.name}</span></div>;
            })}</div>
          )}
        </Sec>
      </main>
    </div>
  );
};

export default HomeTab;
